import type { ReactNode } from "react";

/* Small building blocks shared by the admin screens. */

type Tone = "positive" | "warning" | "negative" | "neutral" | "accent" | "info";

const TONES: Record<Tone, string> = {
  positive: "bg-[#e9f2e2] text-[#3c6a2a]",
  warning: "bg-[#fff1d9] text-[#8a5300]",
  negative: "bg-[#fbe6e3] text-[#a3301f]",
  neutral: "bg-[#f1e9de] text-[#6c574f]",
  accent: "bg-accent-tint text-accent",
  info: "bg-[#e6eefc] text-[#2a5bb8]",
};

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: Tone | undefined;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}

export function KpiCard({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  const bar: Record<Tone, string> = {
    positive: "bg-[#4F7D3A]",
    warning: "bg-[#F99F1C]",
    negative: "bg-[#C0392B]",
    neutral: "bg-[#d8c3ae]",
    accent: "bg-accent",
    info: "bg-[#2f6fdb]",
  };
  return (
    <div className="relative overflow-hidden rounded-[14px] border border-border bg-surface p-4 pl-5">
      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${bar[tone]}`} />
      <div className="text-[11.5px] font-bold uppercase tracking-[0.07em] text-text-muted">
        {label}
      </div>
      <div className="mt-1 font-[family-name:var(--font-display)] text-[28px] font-extrabold leading-none text-text tabular">
        {value}
      </div>
      {hint ? <div className="mt-1.5 text-[12.5px] text-text-muted">{hint}</div> : null}
    </div>
  );
}

/** Six dots for six steps: filled when done, so a row's progress reads at a glance. */
export function StepDots({
  done,
  total = 6,
  label,
}: {
  done: number[];
  total?: number;
  label: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1"
      role="img"
      aria-label={`${label}: ${done.length} of ${total} steps done`}
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`h-2.5 w-2.5 rounded-full ${done.includes(i + 1) ? (i < 4 ? "bg-accent" : "bg-[#4F7D3A]") : "border border-[#d8c3ae] bg-transparent"}`}
        />
      ))}
      <span className="ml-1.5 text-[12px] text-text-muted tabular">
        {done.length}/{total}
      </span>
    </span>
  );
}

export function Avatar({ first, last }: { first: string; last: string }) {
  return (
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-tint text-[12px] font-bold text-accent">
      {(first[0] ?? "?").toUpperCase()}
      {(last[0] ?? "").toUpperCase()}
    </span>
  );
}

/** A chip-style select used in the filter bar. */
export function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | "";
  options: [T, string][];
  onChange: (value: T | "") => void;
}) {
  return (
    <label className="relative inline-flex">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as T | "")}
        className={`h-10 appearance-none rounded-[10px] border-[1.5px] pl-3 pr-8 text-[13.5px] font-semibold ${
          value ? "border-accent bg-accent-tint text-accent" : "border-border bg-surface text-text"
        }`}
      >
        <option value="">{label}: all</option>
        {options.map(([v, t]) => (
          <option key={v} value={v}>
            {label}: {t}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-text-muted"
      >
        ▼
      </span>
    </label>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="relative flex min-w-[220px] flex-1 sm:max-w-[320px]">
      <span className="sr-only">Search</span>
      <svg
        aria-hidden="true"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
      >
        <path
          d="M11 19a8 8 0 100-16 8 8 0 000 16zm10 2l-4.35-4.35"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-[10px] border-[1.5px] border-border bg-surface pl-9 pr-3 text-[13.5px] placeholder:text-[#b9a894] focus:border-accent focus:outline-none"
      />
    </label>
  );
}

export function Pager({
  page,
  total,
  size,
  onPage,
}: {
  page: number;
  total: number;
  size: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / size));
  const from = total ? page * size + 1 : 0;
  const to = Math.min(total, (page + 1) * size);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[13px] text-text-muted">
      <span className="tabular">
        {from}–{to} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          className="h-9 rounded-[9px] border border-border bg-surface px-3 font-semibold text-text disabled:opacity-40"
        >
          ← Previous
        </button>
        <span className="px-2 tabular">
          Page {page + 1} of {pages}
        </span>
        <button
          type="button"
          disabled={page + 1 >= pages}
          onClick={() => onPage(page + 1)}
          className="h-9 rounded-[9px] border border-border bg-surface px-3 font-semibold text-text disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export function ToolbarButton({
  children,
  onClick,
  disabled,
  tone = "ghost",
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "ghost" | "primary";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-10 items-center gap-2 rounded-[10px] border-[1.5px] px-4 text-[13.5px] font-bold disabled:opacity-50 ${
        tone === "primary"
          ? "border-accent bg-accent text-on-accent hover:bg-accent-pressed"
          : "border-border bg-surface text-text hover:border-accent hover:text-accent"
      }`}
    >
      {children}
    </button>
  );
}
